import { useEffect, useState } from 'react'
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom'
import Dashboard from './pages/Dashboard'
import JobList from './pages/JobList'
import JobDetail from './pages/JobDetail'
import JobEditor from './pages/JobEditor'
import RunHistory from './pages/RunHistory'
import GlobalRunHistory from './pages/GlobalRunHistory'
import RunDetail from './pages/RunDetail'
import Reports from './pages/Reports'
import TacticalE2E from './pages/TacticalE2E'
import InternalE2E from './pages/InternalE2E'
import BatchReport from './pages/BatchReport'
import BatchItemDetail from './pages/BatchItemDetail'
import MetricsSettings from './pages/MetricsSettings'
import SystemSettings from './pages/SystemSettings'
import QualitySettings from './pages/QualitySettings'
import SlackSettings from './pages/SlackSettings'
import SfaxAccounts from './pages/SfaxAccounts'
import ProviderAccounts from './pages/ProviderAccounts'
import IdentitySettings from './pages/IdentitySettings'
import AgentSettings from './pages/AgentSettings'
import { AuditLogPage } from './pages/AuditLog'
import { LogViewer } from './pages/LogViewer'
import Help from './pages/Help'
import { AuthProvider } from './auth/AuthContext'
import Layout from './components/Layout'
import { theme } from './theme'
import { EfaxWordmark } from './components/EfaxWordmark'

type AuthState = 'checking' | 'authed'

/** Branded splash shown while the auth check is in flight (and before redirect). */
function BrandedSplash() {
  return (
    <div
      style={{
        minHeight: '100vh',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        gap: '1.25rem',
        background: theme.bg,
        fontFamily: theme.fontFamily,
        color: theme.text,
      }}
    >
      <EfaxWordmark height={56} />
      <div style={{ fontSize: '0.95rem', color: theme.textMuted, letterSpacing: '0.02em' }}>
        E2E Scheduler
      </div>
      <div
        aria-label="Signing in"
        style={{
          width: 28,
          height: 28,
          border: `3px solid ${theme.border}`,
          borderTopColor: theme.primary,
          borderRadius: '50%',
          animation: 'efax-spin 0.8s linear infinite',
        }}
      />
      <div style={{ fontSize: '0.85rem', color: theme.textMuted }}>Signing in…</div>
      <style>{`@keyframes efax-spin { to { transform: rotate(360deg); } }`}</style>
    </div>
  )
}

/**
 * Client-side auth gate. Calls GET /auth/me on mount (NOT under /api).
 * The backend returns 200 with {"authenticated": bool, "user"?: {...}} — it does
 * NOT 401 when logged out — so we branch on the `authenticated` flag in the body,
 * not the HTTP status.
 * - While in flight: render the branded splash (no app data, no flash).
 * - authenticated=false (or any error): redirect to /auth/login.
 * - authenticated=true: render the real app.
 */
function AuthGate({ children }: { children: React.ReactNode }) {
  const [state, setState] = useState<AuthState>('checking')

  useEffect(() => {
    let cancelled = false
    fetch('/auth/me', { credentials: 'same-origin' })
      .then(async (res) => {
        if (cancelled) return
        let authed = false
        if (res.status === 200) {
          try {
            const body = await res.json()
            authed = body?.authenticated === true
          } catch {
            authed = false
          }
        }
        if (authed) {
          setState('authed')
        } else {
          window.location.href = '/auth/login'
        }
      })
      .catch(() => {
        if (cancelled) return
        window.location.href = '/auth/login'
      })
    return () => {
      cancelled = true
    }
  }, [])

  if (state !== 'authed') return <BrandedSplash />
  return <>{children}</>
}

function App() {
  return (
    <AuthGate>
      <BrowserRouter>
        <AuthProvider>
        <Routes>
          <Route element={<Layout />}>
            <Route path="/" element={<Dashboard />} />
            <Route path="/jobs" element={<JobList />} />
            <Route path="/jobs/new" element={<JobEditor />} />
            <Route path="/jobs/:id" element={<JobDetail />} />
            <Route path="/jobs/:id/edit" element={<JobEditor />} />
            <Route path="/jobs/:id/runs" element={<RunHistory />} />
            <Route path="/runs" element={<GlobalRunHistory />} />
            <Route path="/runs/:id" element={<RunDetail />} />
            <Route path="/tactical" element={<TacticalE2E />} />
            {/* DECLARED BEFORE `/tactical/:id`, AND THAT ORDER IS LOAD-BEARING. React
                Router matches in declaration order, so `:id` placed first captures the
                literal string `internal` and renders BatchReport for a batch id that is
                not a UUID — a blank detail page rather than an error, which is the
                worst of the three possible outcomes. Asserted by
                tests/test_tactical_nav_and_route_order.py, which fails if the two are
                swapped. Where this route APPEARS in the nav is declared once, in
                ./tacticalNav, for the same reason the settings menu is derived. */}
            <Route path="/tactical/internal" element={<InternalE2E />} />
            <Route path="/tactical/:id" element={<BatchReport />} />
            <Route path="/tactical/:id/items/:itemId" element={<BatchItemDetail />} />
            <Route path="/reports" element={<Reports />} />
            {/* THE /settings ROUTE TABLE. Where each of these APPEARS in the Settings menu
                is declared once, per path, in `./settingsNav` — Layout.tsx derives the menu
                from it and holds no settings path of its own. Adding a route here without a
                row there fails tests/test_settings_menu_covers_every_route.py, which is the
                drift that left /settings/quality and /settings/audit working but unlinked.

                These paths stay LITERAL rather than generated from that table:
                tests/test_settings_guarantees.py parses this JSX to pair every settings
                surface with its page component and its API, and generating it would blind
                that guard. Routes declare what exists; settingsNav declares where it shows. */}
            <Route path="/settings/metrics" element={<MetricsSettings />} />
            <Route path="/settings/system" element={<SystemSettings />} />
            <Route path="/settings/quality" element={<QualitySettings />} />
            <Route path="/settings/slack" element={<SlackSettings />} />
            {/* The kind-parameterised surface, plus a bare route that asks which kind rather
                than guessing. `/settings/sfax-accounts` STAYS as it is: it is an operator's
                bookmark and 59 tests target the page behind it, so it is an alias that keeps
                working, not a redirect. */}
            <Route path="/settings/provider-accounts" element={<ProviderAccounts />} />
            <Route path="/settings/provider-accounts/:kind" element={<ProviderAccounts />} />
            <Route path="/settings/sfax-accounts" element={<SfaxAccounts />} />
            <Route path="/settings/identity" element={<IdentitySettings />} />
            <Route path="/settings/agents" element={<AgentSettings />} />
            <Route path="/settings/audit" element={<AuditLogPage />} />
            <Route path="/settings/logs" element={<LogViewer />} />
            <Route path="/help" element={<Help />} />
            <Route path="*" element={<Navigate to="/" replace />} />
          </Route>
        </Routes>
        </AuthProvider>
      </BrowserRouter>
    </AuthGate>
  )
}

export default App
